import type { ReactNode } from 'react';

interface SectionHeaderProps {
  badgeIcon: string;
  badgeLabel: string;
  heading: ReactNode;
  subtitle?: ReactNode;
  variant?: 'light' | 'dark';
  align?: 'center' | 'left';
  headingClassName?: string;
  className?: string;
}

const HEADING_BASE =
  'font-serif font-bold leading-snug tracking-[-0.005em] text-[1.625rem] md:text-[1.875rem] lg:text-[2.5rem]';

export default function SectionHeader({
  badgeIcon,
  badgeLabel,
  heading,
  subtitle,
  variant = 'light',
  align = 'center',
  headingClassName,
  className,
}: SectionHeaderProps) {
  const isDark = variant === 'dark';
  const isCenter = align === 'center';

  const wrapper = `flex flex-col ${isCenter ? 'items-center text-center' : 'items-start text-left'} ${className ?? ''}`;

  const badge = isDark
    ? 'border border-white/10 bg-white/[0.07] text-[#C9A961]'
    : 'border border-[#8B1538]/20 bg-white/70 text-[#8B1538]';

  const headingColor = isDark ? 'text-white' : 'text-[#1A2B3C]';
  const subtitleColor = isDark ? 'text-white/70' : 'text-[#1A2B3C]/70';

  return (
    <div className={wrapper}>
      <div
        className={`mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 backdrop-blur-sm ${badge}`}
      >
        <i className={`${badgeIcon} flex h-4 w-4 items-center justify-center`}></i>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] lg:text-[13px]">{badgeLabel}</span>
      </div>

      <h2
        className={`mb-5 ${HEADING_BASE} ${headingColor} ${headingClassName ?? ''}`}
        style={{ fontFamily: 'Lora, serif' }}
      >
        {heading}
      </h2>

      <div className="mb-6 h-px w-12 bg-[#C9A961]" />

      {subtitle && (
        <div className={`max-w-2xl text-sm leading-relaxed lg:text-base ${subtitleColor}`}>
          {subtitle}
        </div>
      )}
    </div>
  );
}
